/**
 * PlaceAutocompleteInput: drop-in text input with debounced place
 * suggestions backed by /api/locations/search (OSM Nominatim proxy).
 *
 * Why a custom component instead of a third-party combobox?
 *   - The free-text fallback is critical: rural PH addresses often don't
 *     show up in OSM. Users must be able to type and continue even if no
 *     suggestion appears.
 *   - Provider 503 (rate-limited cloud egress in preview) must NOT block
 *     the form. We surface a quiet "enter manually" hint instead.
 *   - Selecting a suggestion populates lat/lng. Free text leaves them null
 *     so downstream code can flag "unverified" locations.
 *
 * Value shape (always):
 *   { label, address, lat, lng, place_id, osm_id, osm_type, provider, manual }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Loader2, X, Check, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const DEBOUNCE_MS = 350;

export default function PlaceAutocompleteInput({
  value = null,
  onChange,
  label = "Location",
  placeholder = "Start typing a place or address…",
  countryCode = "ph",
  viewbox = null, // optional "minLon,maxLat,maxLon,minLat" to bias results
  required = false,
  disabled = false,
  className = "",
  testId = "place-autocomplete",
  helperText = null,
  showCoordinatesHint = true,
}) {
  const [query, setQuery] = useState(value?.label || value?.address || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerError, setProviderError] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [touched, setTouched] = useState(false);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");

  // If parent value changes (e.g., reset), reflect it in the input.
  useEffect(() => {
    setQuery(value?.label || value?.address || "");
  }, [value?.label, value?.address]);

  const isSelected = !!(value?.lat && value?.lng && !value?.manual);
  const isManual = !!(value?.manual || (value && query && (!value.lat || !value.lng)));

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    lastQueryRef.current = trimmed;
    if (trimmed.length < 2) {
      setResults([]); setLoading(false); return;
    }
    setLoading(true); setProviderError(false);
    try {
      const params = { q: trimmed, country: countryCode, limit: 8 };
      if (viewbox) params.viewbox = viewbox;
      const r = await axios.get(`${API}/locations/search`, { params });
      if (lastQueryRef.current !== trimmed) return; // stale
      setResults(r.data?.items || []);
    } catch (e) {
      if (lastQueryRef.current !== trimmed) return;
      // 429/503/etc: degrade gracefully. The free-text input still works.
      setResults([]);
      setProviderError(true);
    } finally {
      if (lastQueryRef.current === trimmed) setLoading(false);
    }
  }, [countryCode, viewbox]);

  // Debounced search on query change
  useEffect(() => {
    if (!touched) return; // don't auto-fire when we hydrated from `value`
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, touched, runSearch]);

  // Close the suggestions on outside-click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setHighlight(-1);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const select = (item) => {
    onChange?.({
      label: item.display_name,
      address: item.display_name,
      lat: item.lat,
      lng: item.lng,
      place_id: item.place_id,
      osm_id: item.osm_id,
      osm_type: item.osm_type,
      provider: item.provider || "nominatim",
      manual: false,
    });
    setQuery(item.display_name);
    setOpen(false);
    setHighlight(-1);
  };

  const clear = () => {
    onChange?.(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    setHighlight(-1);
  };

  // "Use as manual": keep typed text but flag as unverified.
  const useManual = () => {
    if (!query.trim()) return;
    onChange?.({
      label: query.trim(),
      address: query.trim(),
      lat: null,
      lng: null,
      provider: "manual",
      manual: true,
    });
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlight >= 0) {
        e.preventDefault();
        select(results[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlight(-1);
    }
  };

  const ariaLabel = label || "Place autocomplete";
  const inputId = useMemo(() => `${testId}-input-${Math.random().toString(36).slice(2, 7)}`, [testId]);

  return (
    <div className={`relative ${className}`} ref={wrapperRef} data-testid={testId}>
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
          {label}{required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          id={inputId}
          data-testid={`${testId}-text`}
          type="text"
          aria-label={ariaLabel}
          autoComplete="off"
          spellCheck={false}
          required={required}
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          onChange={(e) => { setQuery(e.target.value); setTouched(true); setOpen(true); }}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKey}
          className={`w-full pl-9 pr-9 py-2.5 rounded-lg border outline-none transition-colors ${
            isSelected
              ? "border-emerald-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              : isManual
              ? "border-amber-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              : "border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F]"
          }`}
        />
        {loading && (
          <Loader2 className="w-4 h-4 text-slate-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
        )}
        {!loading && (value?.lat || value?.label || query) && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testId}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
            title="Clear"
          >
            <X className="w-3.5 h-3.5 text-slate-500" />
          </button>
        )}
      </div>

      {/* Status row */}
      <div className="mt-1.5 min-h-[18px] text-xs">
        {isSelected ? (
          <span data-testid={`${testId}-selected`} className="text-emerald-700 inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Place selected
            {showCoordinatesHint && value.lat && value.lng && (
              <span className="text-emerald-600/80 ml-1">({value.lat.toFixed(4)}, {value.lng.toFixed(4)})</span>
            )}
          </span>
        ) : isManual ? (
          <span data-testid={`${testId}-manual`} className="text-amber-700 inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Manual entry: distance &amp; map features may be unavailable.
          </span>
        ) : helperText ? (
          <span className="text-slate-500">{helperText}</span>
        ) : null}
      </div>

      {/* Suggestions dropdown */}
      {open && (results.length > 0 || providerError || (query.trim().length >= 2 && !loading)) && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-[280px] overflow-auto"
          role="listbox"
        >
          {results.map((item, idx) => (
            <button
              key={`${item.place_id}-${idx}`}
              type="button"
              role="option"
              aria-selected={highlight === idx}
              data-testid={`${testId}-option-${idx}`}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => select(item)}
              className={`w-full text-left px-3 py-2 text-sm border-b border-slate-50 last:border-0 ${
                highlight === idx ? "bg-emerald-50" : "hover:bg-slate-50"
              }`}
            >
              <div className="text-slate-900 leading-tight">{item.display_name}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {item.lat?.toFixed(4)}, {item.lng?.toFixed(4)}
                {item.type && <span className="ml-2 capitalize">{item.type.replace(/_/g, " ")}</span>}
              </div>
            </button>
          ))}

          {!loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="px-3 py-3 text-sm">
              <div className="text-slate-600">
                {providerError
                  ? "Couldn't reach the place search right now."
                  : "No matching place found."}
              </div>
              <button
                type="button"
                data-testid={`${testId}-use-manual`}
                onClick={useManual}
                className="mt-2 text-emerald-700 hover:underline text-xs font-medium"
              >
                Use "{query.trim()}" as a manual address →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
